/**
 * Percentile lookup: map a runner's PB to a population percentile rank.
 *
 * Uses the RunRepeat distribution cutoffs (see percentile-data.ts) and
 * linear interpolation between adjacent published percentile points.
 * Convention: a HIGHER returned percentile means FASTER (beats more runners).
 */

import type {
  AgeGroup,
  EquivalentPerformance,
  PercentileComparisonRow,
  PercentilePoint,
  PercentileResult,
  PercentileSource,
  RaceDistance,
  RunnerCohort,
} from '../shared/types';
import {
  COHORT_LABELS,
  getDistribution,
  PERCENTILE_DISTANCES,
  RACE_DISTRIBUTION_NAMES,
} from './percentile-data';
import {
  MAJORS_AGE_GROUPS,
  MAJORS_COHORT_SIZES,
  MAJORS_GRID,
  MAJORS_TABLE,
} from './percentile-data-majors';

// re-export so callers can import everything percentile-related from one module
export {
  COHORT_LABELS,
  getDistribution,
  hasDistribution,
  PERCENTILE_DISTANCES,
} from './percentile-data';
export { MAJORS_AGE_GROUPS } from './percentile-data-majors';

/** Map a RunnerCohort ('men'/'women') to the Majors dataset's gender key. */
function cohortToGender(cohort: RunnerCohort): 'Men' | 'Women' | null {
  if (cohort === 'men') return 'Men';
  if (cohort === 'women') return 'Women';
  return null; // 'overall' has no Majors equivalent
}

/** Does the Majors dataset have data for this cohort + age group? */
export function hasMajors(cohort: RunnerCohort, ageGroup: AgeGroup): boolean {
  const gender = cohortToGender(cohort);
  return Boolean(gender && MAJORS_TABLE[gender]?.[ageGroup]);
}

/**
 * Convert a Majors cohort's cutoff map into the PercentilePoint[] shape that
 * the shared interpolation engine consumes (points ordered fastest→slowest,
 * i.e. lowest time first / highest grid-percentile first).
 */
function majorsToPoints(cohort: RunnerCohort, ageGroup: AgeGroup): PercentilePoint[] | null {
  const gender = cohortToGender(cohort);
  if (!gender) return null;
  const cutoffs = MAJORS_TABLE[gender]?.[ageGroup];
  if (!cutoffs) return null;
  const points: PercentilePoint[] = [];
  for (const p of MAJORS_GRID) {
    const t = cutoffs[String(p)];
    if (typeof t === 'number') points.push({ percentile: p, timeSeconds: t });
  }
  return points;
}

/**
 * Lightweight Majors percentile lookup: rank (0–100, higher = faster) for a
 * marathon finish time against a gender × age-group cohort. Returns null if
 * the cohort doesn't exist (e.g. 'overall', or a dropped small cohort).
 */
export function rankForMajors(
  timeSeconds: number,
  cohort: RunnerCohort,
  ageGroup: AgeGroup,
): number | null {
  const points = majorsToPoints(cohort, ageGroup);
  if (!points) return null;
  return interpolatePercentile(timeSeconds, points);
}

/**
 * Full Majors percentile result (mirrors percentileFor but for the World
 * Majors age-graded dataset). Marathon only.
 */
export function percentileForMajors(
  pbSeconds: number,
  cohort: RunnerCohort,
  ageGroup: AgeGroup,
): PercentileResult | null {
  const points = majorsToPoints(cohort, ageGroup);
  if (!points) return null;
  const gender = cohortToGender(cohort)!;
  const pct = interpolatePercentile(pbSeconds, points);
  const n = MAJORS_COHORT_SIZES[gender]?.[ageGroup];
  return {
    distance: 'marathon',
    cohort,
    source: 'majors',
    ageGroup,
    cohortSize: n,
    pbSeconds,
    percentile: pct,
    distribution: points,
    summary: summarizeMajors(cohort, ageGroup, pct),
  };
}

/**
 * Lightweight percentile lookup: returns just the rank (0–100) for a finish
 * time against a distance+cohort, or null if no distribution exists.
 */
export function rankFor(
  timeSeconds: number,
  distance: RaceDistance,
  cohort: RunnerCohort,
): number | null {
  const dist = getDistribution(distance, cohort);
  if (!dist) return null;
  return interpolatePercentile(timeSeconds, dist);
}

/**
 * Compute a runner's percentile rank (0–100, higher = faster) for a given
 * finish time against a distribution. Returns null if no distribution exists
 * for the distance + cohort.
 *
 * The distribution is a set of (percentile, time) points where higher
 * percentile = faster (lower time). We find the two points bracketing the
 * PB time and linearly interpolate the percentile.
 */
export function percentileFor(
  pbSeconds: number,
  distance: RaceDistance,
  cohort: RunnerCohort,
): PercentileResult | null {
  const dist = getDistribution(distance, cohort);
  if (!dist) return null;

  const pct = interpolatePercentile(pbSeconds, dist);
  return {
    distance,
    cohort,
    source: 'all',
    pbSeconds,
    percentile: pct,
    distribution: dist,
    summary: summarize(distance, cohort, pct),
  };
}

/**
 * Given a finish time and a percentile distribution (points ordered P1..P90,
 * i.e. times strictly increasing), return the runner's percentile rank
 * where HIGHER rank = FASTER (beats that % of the field).
 *
 * The grid stores percentile cutoffs in the source convention (P1 = fastest,
 * P90 = slowest). A runner's "rank" is the % of runners they beat, which is
 * the complement: rank = 100 − gridPercentile. So:
 *   - finishing exactly at the P1 cutoff time  -> rank 99
 *   - finishing exactly at the P50 (median) time -> rank 50
 *   - finishing exactly at the P90 cutoff time  -> rank 10
 *
 * Within the measured range we linearly interpolate between adjacent cutoffs.
 * Outside it we extrapolate in the *odds* domain (logit of rank) using the
 * nearest segment's slope. Finish-time distributions are roughly log-normal,
 * so tail probabilities decay geometrically — linear-in-odds extrapolation is
 * far less aggressive than linear-in-rank (it never blows past 100% or below
 * 0%), and it makes a sub-P1 performance like a 19:47 women's 5K land at a
 * believable ~99.5% instead of a nonsensical "100%".
 */
export function interpolatePercentile(
  timeSeconds: number,
  dist: PercentilePoint[],
): number {
  // Convert the grid to rank space: {time, rank} where rank = 100 - gridPct.
  // Ranks are DECREASING along the array (99 at index 0 … 10 at end),
  // times are strictly increasing.
  const pts = dist.map((p) => ({ t: p.timeSeconds, rank: 100 - p.percentile }));

  const fastest = pts[0]!; // lowest time, highest rank (~99)
  const slowest = pts[pts.length - 1]!; // highest time, lowest rank (~10)

  // Faster than the fastest published cutoff -> extrapolate rank upward (in odds).
  if (timeSeconds <= fastest.t) {
    const next = pts[1] ?? fastest;
    return extrapolateRank(fastest, next, timeSeconds, 'up');
  }

  // Slower than the slowest published cutoff -> extrapolate rank downward (in odds).
  if (timeSeconds >= slowest.t) {
    const prev = pts[pts.length - 2] ?? slowest;
    return extrapolateRank(slowest, prev, timeSeconds, 'down');
  }

  // Bracket: find segment [a, b] with a.t <= time <= b.t, then interpolate rank.
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    if (timeSeconds >= a.t && timeSeconds <= b.t) {
      const frac = (timeSeconds - a.t) / (b.t - a.t);
      // Time rises a->b; rank falls a.rank->b.rank.
      return a.rank + frac * (b.rank - a.rank);
    }
  }
  return 50; // unreachable given the clamps above
}

/**
 * Tail extrapolation in the log-odds (logit) domain.
 *
 * Maps rank r to its odds o = r/(100−r), takes log, then linearly
 * extrapolates using the slope between `here` and `anchor` (the two nearest
 * published points). Converting back keeps the result strictly inside (0,100),
 * so it can never print "100%". Times: `here` is the nearest grid point to the
 * runner (fastest cutoff for the up tail, slowest for the down tail), `anchor`
 * is the next one inward.
 */
function extrapolateRank(
  here: { t: number; rank: number },
  anchor: { t: number; rank: number },
  timeSeconds: number,
  dir: 'up' | 'down',
): number {
  if (here.rank <= 0 || here.rank >= 100) return clamp(here.rank, 0.1, 99.9);
  const dTime = Math.abs(anchor.t - here.t);
  if (dTime === 0) return clamp(here.rank, 0.1, 99.9);

  const odds = (r: number) => r / (100 - r);
  const logit = (r: number) => Math.log(odds(r));

  const slope = (logit(here.rank) - logit(anchor.rank)) / (here.t - anchor.t);
  // dir 'up': timeSeconds < here.t, rank rises above here.rank
  // dir 'down': timeSeconds > here.t, rank falls below here.rank
  const extrapolatedLogit = logit(here.rank) + slope * (timeSeconds - here.t);
  // Convert logit back to rank: r = 100 * e^L / (1 + e^L)
  const e = Math.exp(extrapolatedLogit);
  const rank = (100 * e) / (1 + e);
  // Loosen the cap: logit-space already bounds to (0,100); keep a small floor.
  return clamp(rank, dir === 'up' ? here.rank : 0.1, dir === 'up' ? 99.9 : here.rank);
}

/**
 * Format a percentile rank for display, always showing a decimal once the
 * value crosses 99% (or drops below 1%) so we never show an impossible
 * "100%"/"0%". Between 1% and 99% we show a rounded integer.
 *
 *   99.0  -> "99.0%"     99.94 -> "99.9%"     76.3 -> "76%"
 *   0.8   -> "0.8%"      0.05 -> "0.1%"
 */
export function formatPercentile(rank: number): string {
  if (rank >= 99) return `${clamp(rank, 0, 99.9).toFixed(1)}%`;
  if (rank <= 1) return `${clamp(rank, 0.1, 1).toFixed(1)}%`;
  return `${Math.round(rank)}%`;
}

/** Summary string: "Faster than 73% of male marathoners." */
function summarize(distance: RaceDistance, cohort: RunnerCohort, percentile: number): string {
  const cohortPhrase =
    cohort === 'overall'
      ? `${RACE_DISTRIBUTION_NAMES[distance]} runners overall`
      : `${cohort === 'men' ? 'male' : 'female'} ${RACE_DISTRIBUTION_NAMES[distance]} runners`;
  return `Faster than ≈${formatPercentile(percentile)} of ${cohortPhrase}.`;
}

/** Summary string for the World Majors age-graded baseline. */
function summarizeMajors(cohort: RunnerCohort, ageGroup: AgeGroup, percentile: number): string {
  const sex = cohort === 'men' ? 'male' : 'female';
  return `Faster than ≈${formatPercentile(percentile)} of ${sex} World-Majors marathoners aged ${ageGroup}.`;
}

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Build the multi-distance percentile comparison.
 *
 * For the runner's actual PB distance we use the real finish time. For every
 * OTHER distance that has a distribution, we take the VDOT-projected equivalent
 * time (from the VDOT engine's `equivalents`) and rank that against the field.
 * Projected rows are flagged so the UI can mark them with an asterisk/footnote.
 *
 * Returns rows ordered as in PERCENTILE_DISTANCES, with the input distance
 * always first.
 */
export function compareDistances(
  inputDistance: RaceDistance,
  inputTimeSeconds: number,
  equivalents: EquivalentPerformance[],
  cohort: RunnerCohort,
): PercentileComparisonRow[] {
  // Index equivalents by distance for quick lookup.
  const eqByDist = new Map<RaceDistance, number>();
  for (const e of equivalents) eqByDist.set(e.distance, e.timeSeconds);

  // Build the ordered list of distances to show: input first (if it has data),
  // then the rest of the percentile distances.
  const order: RaceDistance[] = [];
  if (getDistribution(inputDistance, cohort)) order.push(inputDistance);
  for (const d of PERCENTILE_DISTANCES) {
    if (d !== inputDistance) order.push(d);
  }

  const rows: PercentileComparisonRow[] = [];
  for (const distance of order) {
    // The input distance uses the real PB; everything else is projected.
    const projected = distance !== inputDistance;
    const timeSeconds = projected ? (eqByDist.get(distance) ?? NaN) : inputTimeSeconds;
    if (!Number.isFinite(timeSeconds)) continue;

    const rank = rankFor(timeSeconds, distance, cohort);
    rows.push({
      distance,
      timeSeconds,
      projected,
      percentile: rank,
      summary: rowSummary(distance, cohort, rank, projected),
    });
  }
  return rows;
}

/** Row summary, with an asterisk on projected (non-PB) rows. */
function rowSummary(
  distance: RaceDistance,
  cohort: RunnerCohort,
  rank: number | null,
  projected: boolean,
): string {
  if (rank === null) {
    return `No distribution data for ${RACE_DISTRIBUTION_NAMES[distance]}.`;
  }
  const cohortPhrase =
    cohort === 'overall'
      ? `${RACE_DISTRIBUTION_NAMES[distance]} runners overall`
      : `${cohort === 'men' ? 'male' : 'female'} ${RACE_DISTRIBUTION_NAMES[distance]} runners`;
  const tag = projected ? '*' : '';
  return `Faster than ≈${formatPercentile(rank)} of ${cohortPhrase}.${tag}`;
}
